import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import StatusPostItem from '../components/StatusPostItem';
import Spinner from '../components/Spinner';
import { toast } from 'react-toastify';
import { useStatusContext } from '../utils/StatusContext';
import { getAuth } from 'firebase/auth';
import { FaSearch } from 'react-icons/fa';

export default function StatusPage() {
  const auth = getAuth();
  const [statuses, setStatuses] = useState([]);
  const [filteredStatuses, setFilteredStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStatusId, setActiveStatusId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { markStatusAsViewed } = useStatusContext();

  // Fetch statuses with real-time updates (without 24-hour expiration)
  useEffect(() => {
    setLoading(true);
    const statusQuery = query(
      collection(db, 'status'),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(statusQuery, 
      (snapshot) => {
        const statusData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          viewedBy: doc.data().viewedBy || [],
          likedBy: doc.data().likedBy || [],
          savedBy: doc.data().savedBy || []
        }));
        setStatuses(statusData);
        setFilteredStatuses(statusData); // Initialize filtered statuses
        setLoading(false);
      },
      (error) => {
        toast.error('Failed to load statuses');
        console.error('Error fetching statuses:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Filter statuses based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredStatuses(statuses);
      return;
    }

    const queryLower = searchQuery.toLowerCase();
    const filtered = statuses.filter(status => {
      // Search in status text
      const statusTextMatch = status.text?.toLowerCase().includes(queryLower);
      
      // Search in user data (name and username)
      const userData = status.userData; // Assuming userData is populated in StatusPostItem
      const userNameMatch = userData?.name?.toLowerCase().includes(queryLower);
      const usernameMatch = userData?.username?.toLowerCase().includes(queryLower);

      return statusTextMatch || userNameMatch || usernameMatch;
    });

    setFilteredStatuses(filtered);
  }, [searchQuery, statuses]);

  // Handle status view
  const handleStatusView = async (statusId) => {
    if (!auth.currentUser) return;
    
    try {
      setActiveStatusId(statusId);
      await markStatusAsViewed(statusId);
      // Additional logic if needed after marking as viewed
    } catch (error) {
      console.error('Error handling status view:', error);
    } finally {
      setActiveStatusId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-black py-4 px-4">
      <h1 className="text-white text-2xl font-bold mb-2">Status Updates</h1>
      
      {/* Search Bar */}
      <div className="mb-2">
        <div className="relative max-w-xl">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Search statuses by their captions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 text-white pl-10 pr-4 py-2 rounded-full focus:outline-none focus:border-[#FF1493]"
          />
        </div>
      </div>
      
      {filteredStatuses.length > 0 ? (
        <div className="space-y-4">
          {filteredStatuses.map(status => (
            <StatusPostItem 
              key={status.id} 
              status={status} 
              onView={() => handleStatusView(status.id)}
              isActive={activeStatusId === status.id}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-[60vh]">
          {searchQuery ? (
            <p className="text-gray-400 text-lg mb-4">No statuses match your search</p>
          ) : (
            <>
              <p className="text-gray-400 text-lg mb-4">No status updates available</p>
              <p className="text-gray-500 text-sm">
                {auth.currentUser ? 'Upload a status to share with your followers' : 'Sign in to view status updates'}
              </p>
            </>
          )}
        </div>
      )}
    </main>
  );
}